/**
 * Rack edit form body — collapsible "Racks" + "Rack Power Information" + Additional Information (#995 / #1052).
 * Layout matches Create Request rack cards (two-column web grid, fill-count headers).
 */

import React, { useState } from 'react';
import { Platform, StyleSheet, View } from 'react-native';

import { parseOptionalNumber } from './rackListUtils';
import {
  RACK_EDIT_POWER_CARD_SUBTITLE,
  RACK_EDIT_RACKS_CARD_SUBTITLE,
  RACK_EDIT_RACK_TYPE_LABEL,
  RACK_EDIT_RACK_TYPE_OPTIONS,
  RACK_EDIT_STATUS_EXISTING_LABEL,
} from '../../constants/rackConfigurationsSubsectionCopy';
import { RACK_POWER_FIELD_LABELS } from '../../constants/rackPowerFieldLabels';
import {
  RACK_SUBSECTIONS_RACK_FIELD_INFO,
  rackSubsectionsRackInfoA11y,
  type RackSubsectionsRackInfoKey,
} from '../../constants/rackPowerRackCardFieldInfo';
import {
  RACK_MODAL_RACKS_FIELDS,
  RACK_MODAL_POWER_FIELDS,
  RACK_POWER_NOTES_SUBSECTION_FIELDS,
} from '../../constants/requestFieldDescriptors';
import { SECTION_IDS, getSectionLabel } from '../../constants/sectionIds';
import { useFormFieldVisibilityContext } from '../../hooks/useFormFieldVisibilityContext';
import { RequestFormAdditionalInformation } from '../../request/steps/subsections/AdditionalInformation';
import { colors, spacing } from '../../theme';
import {
  RACK_PLATFORM_OPTIONS,
  RACK_MAX_LOAD_RATING_OPTIONS,
  RACK_COLOR_OPTIONS,
  RACK_PDU_OUTLET_TYPE_OPTIONS,
  RACK_PDU_MOUNTING_TYPE_OPTIONS,
} from '../../types/rack';
import {
  selectAttachmentsForRepeatableInstance,
  selectSelectedFilesForRepeatableInstance,
  resolveRepeatableInstanceAttachmentSectionId,
} from '../../utils/repeatableInstanceAttachmentSections';
import { subsectionHeaderMetricsProps } from '../../utils/subsectionHeaderMetrics';
import { booleanToYesNo } from '../../utils/yesNoBoolean';
import {
  FormRow,
  FormColumn,
  FormInput,
  FormDropdown,
  FormDropdownField,
  FormYesNoChoice,
  FormTwoOptionChoice,
  NumberInput,
  UnitNumberField,
  formFieldInStack,
  FormStackColumnsOnWebProvider,
  StepForm,
  FormInfoHelpModal,
} from '../forms';
import { LocationTagPicker } from '../locationTags/LocationTagPicker';
import { PermissionAwareField } from '../PermissionAwareField';

import type { SavedAttachment } from '../../services/attachmentService';
import type { RackStatus } from '../../types/rack';
import type { RackConfiguration } from '../../types/rack';
import type { SelectedFile } from '../forms/FileUpload';

const AC_DC_POWER_OPTIONS = ['AC', 'DC'];

function parsePowerType(s: unknown): string[] {
  if (s == null || s === '') return [];
  return String(s)
    .split(',')
    .map(x => x.trim())
    .filter(Boolean);
}

/**
 * Serialize a multi-select AC/DC selection to the canonical comma-joined
 * storage form (`'AC'`, `'DC'`, `'AC, DC'`, `''`). Mirrors
 * `serializeAcDcSelection` in `server/constants/pdu.ts` (parity-by-review).
 * #1012 Item 2b.
 */
function serializePowerType(values: readonly string[]): string {
  const seen = new Set<string>();
  const ordered: string[] = [];
  for (const v of values) {
    if (!seen.has(v) && (v === 'AC' || v === 'DC')) {
      seen.add(v);
      ordered.push(v);
    }
  }
  ordered.sort((a, b) => (a === 'AC' ? -1 : b === 'AC' ? 1 : 0));
  return ordered.join(', ');
}

export interface RackEditFormAttachmentProps {
  attachmentsBySection?: Record<string, SavedAttachment[]>;
  onAttachFiles?: (sectionId: string) => void;
  onDeleteAttachment?: (attachmentId: number) => void;
  selectedFiles?: Record<string, SelectedFile[]>;
  onFilesSelected?: (sectionId: string, files: SelectedFile[]) => void;
  isOffline?: boolean;
  onCaptionChange?: (attachmentId: number, caption: string) => void;
  pendingCaptionEdits?: Record<number, string>;
}

export interface RackEditFormContentProps extends RackEditFormAttachmentProps {
  formData: Partial<RackConfiguration>;
  updateField: <K extends keyof RackConfiguration>(
    field: K,
    value: RackConfiguration[K],
  ) => void;
  disabled?: boolean;
  requestStage?: string;
  requestId?: string;
  showFullDetails?: boolean;
  testID?: string;
}

function rackStatusToTypeLabel(status: RackStatus | undefined): string {
  return status === 'existing'
    ? RACK_EDIT_RACK_TYPE_OPTIONS[0]
    : RACK_EDIT_RACK_TYPE_OPTIONS[1];
}

function rackTypeLabelToStatus(label: string): RackStatus {
  return label === RACK_EDIT_STATUS_EXISTING_LABEL ? 'existing' : 'new';
}

function RackPermissionField({
  fieldId,
  requestStage,
  requestId,
  children,
}: {
  fieldId: string;
  requestStage?: string;
  requestId?: string;
  children: React.ReactNode;
}) {
  if (!requestStage) {
    return <>{children}</>;
  }
  return (
    <PermissionAwareField
      fieldId={fieldId}
      requestStage={requestStage}
      requestId={requestId}
      containerStyle={formFieldInStack}
    >
      {children}
    </PermissionAwareField>
  );
}

const RACK_EDITOR_SUBSECTION_PERSIST = {
  persistSectionExpanded: true,
} as const;

export const RackEditFormContent: React.FC<RackEditFormContentProps> = ({
  formData,
  updateField,
  disabled = false,
  requestStage,
  requestId,
  showFullDetails = true,
  attachmentsBySection,
  onAttachFiles,
  onDeleteAttachment,
  selectedFiles,
  onFilesSelected,
  isOffline = false,
  onCaptionChange,
  pendingCaptionEdits,
  testID = 'rack-edit-form',
}) => {
  const fieldVisibility = useFormFieldVisibilityContext({
    showFullDetails,
    requestStage: requestStage ?? 'DRAFT',
    requestId,
  });
  const headerValues = formData as Record<string, unknown>;
  const racksMetrics = subsectionHeaderMetricsProps(
    RACK_MODAL_RACKS_FIELDS,
    headerValues,
    fieldVisibility,
  );
  const powerMetrics = subsectionHeaderMetricsProps(
    RACK_MODAL_POWER_FIELDS,
    headerValues,
    fieldVisibility,
  );
  const [racksHelpKey, setRacksHelpKey] =
    useState<RackSubsectionsRackInfoKey | null>(null);
  const permissionStage = requestStage ?? 'DRAFT';
  /** Match other steps (Cooling, etc.): header chrome is not gated on persisted id. */
  const attachmentsEnabled = true;
  const configurationId = formData.id;
  const racksBaseSectionId = SECTION_IDS.RACK_CONFIGURATIONS;
  const powerBaseSectionId = SECTION_IDS.RACK_POWER_INFO;
  const additionalBaseSectionId = SECTION_IDS.RACKS_ADDITIONAL;
  const racksSectionId = resolveRepeatableInstanceAttachmentSectionId(
    racksBaseSectionId,
    configurationId,
  );
  const powerSectionId = resolveRepeatableInstanceAttachmentSectionId(
    powerBaseSectionId,
    configurationId,
  );
  const additionalSectionId = resolveRepeatableInstanceAttachmentSectionId(
    additionalBaseSectionId,
    configurationId,
  );
  const racksAttachments = selectAttachmentsForRepeatableInstance(
    attachmentsBySection ?? {},
    racksBaseSectionId,
    configurationId,
  );
  const racksSelectedFiles = selectSelectedFilesForRepeatableInstance(
    selectedFiles ?? {},
    racksBaseSectionId,
    configurationId,
  );
  const powerAttachments = selectAttachmentsForRepeatableInstance(
    attachmentsBySection ?? {},
    powerBaseSectionId,
    configurationId,
  );
  const powerSelectedFiles = selectSelectedFilesForRepeatableInstance(
    selectedFiles ?? {},
    powerBaseSectionId,
    configurationId,
  );
  const additionalAttachments = selectAttachmentsForRepeatableInstance(
    attachmentsBySection ?? {},
    additionalBaseSectionId,
    configurationId,
  );
  const additionalSelectedFiles = selectSelectedFilesForRepeatableInstance(
    selectedFiles ?? {},
    additionalBaseSectionId,
    configurationId,
  );

  const rackHeightLabel = 'Height (RU/OU)';
  const depthLabel = 'Depth';
  const widthLabel = 'Width';

  return (
    <FormStackColumnsOnWebProvider enabled={Platform.OS === 'web'}>
      <FormInfoHelpModal
        visible={racksHelpKey != null}
        onClose={() => setRacksHelpKey(null)}
        title={
          racksHelpKey != null
            ? RACK_SUBSECTIONS_RACK_FIELD_INFO[racksHelpKey].title
            : ''
        }
        bullets={
          racksHelpKey != null
            ? [...RACK_SUBSECTIONS_RACK_FIELD_INFO[racksHelpKey].bullets]
            : []
        }
      />
      <StepForm
        title={getSectionLabel(racksSectionId)}
        titleSubtitle={RACK_EDIT_RACKS_CARD_SUBTITLE}
        defaultSectionExpanded
        {...RACK_EDITOR_SUBSECTION_PERSIST}
        {...racksMetrics}
        sectionId={racksSectionId}
        requestId={requestId}
        requestStage={permissionStage}
        allowAttachments={attachmentsEnabled}
        attachments={racksAttachments}
        onAttachFiles={
          onAttachFiles ? () => onAttachFiles(racksSectionId) : undefined
        }
        onDeleteAttachment={onDeleteAttachment}
        selectedFiles={racksSelectedFiles}
        onFilesSelected={
          onFilesSelected
            ? files => onFilesSelected(racksSectionId, files)
            : undefined
        }
        isOffline={isOffline}
        onCaptionChange={onCaptionChange}
        pendingCaptionEdits={pendingCaptionEdits}
        isEditing={!disabled}
        disabled={disabled}
        containerStyle={styles.sectionCard}
      >
        <FormRow>
          <FormColumn>
            <RackPermissionField
              fieldId="rackName"
              requestStage={requestStage}
              requestId={requestId}
            >
              <FormInput
                label="Rack Name"
                value={formData.rackName || ''}
                onChangeText={value => updateField('rackName', value)}
                placeholder="Enter a name for this rack (e.g., Server Rack A)"
                disabled={disabled}
                testID={`${testID}-rack-name`}
                containerStyle={formFieldInStack}
              />
            </RackPermissionField>
          </FormColumn>
          <FormColumn>
            <RackPermissionField
              fieldId="rackStatus"
              requestStage={requestStage}
              requestId={requestId}
            >
              <FormTwoOptionChoice
                label={RACK_EDIT_RACK_TYPE_LABEL}
                value={rackStatusToTypeLabel(formData.rackStatus)}
                options={[
                  RACK_EDIT_RACK_TYPE_OPTIONS[0],
                  RACK_EDIT_RACK_TYPE_OPTIONS[1],
                ]}
                onSelect={label =>
                  updateField('rackStatus', rackTypeLabelToStatus(label))
                }
                disabled={disabled}
                inline
                testID={`${testID}-rack-type`}
                containerStyle={formFieldInStack}
              />
            </RackPermissionField>
          </FormColumn>
        </FormRow>

        <FormRow>
          <FormColumn>
            <RackPermissionField
              fieldId="locationTagId"
              requestStage={requestStage}
              requestId={requestId}
            >
              <LocationTagPicker
                requestId={requestId}
                requestStage={requestStage}
                value={formData.locationTagId ?? null}
                onChange={tagId => updateField('locationTagId', tagId)}
                disabled={disabled}
                testID={`${testID}-location-tag`}
                containerStyle={formFieldInStack}
              />
            </RackPermissionField>
          </FormColumn>
        </FormRow>

        <FormRow>
          <FormColumn>
            <RackPermissionField
              fieldId="rackPlatform"
              requestStage={requestStage}
              requestId={requestId}
            >
              <FormDropdownField
                label="Rack Platform"
                value={formData.rackPlatform || ''}
                options={[...RACK_PLATFORM_OPTIONS]}
                onSelect={value =>
                  updateField(
                    'rackPlatform',
                    Array.isArray(value) ? value[0] ?? '' : value,
                  )
                }
                placeholder="Select..."
                disabled={disabled}
                allowClear
                containerStyle={formFieldInStack}
              />
            </RackPermissionField>
          </FormColumn>
          <FormColumn>
            <RackPermissionField
              fieldId="rackDescription"
              requestStage={requestStage}
              requestId={requestId}
            >
              <FormInput
                label="Description"
                value={formData.rackDescription || ''}
                onChangeText={value => updateField('rackDescription', value)}
                placeholder=""
                multiline
                maxLength={10000}
                richText
                requestId={requestId}
                requestSection={racksSectionId}
                isOnline={!isOffline}
                disabled={disabled}
                containerStyle={formFieldInStack}
              />
            </RackPermissionField>
          </FormColumn>
        </FormRow>

        <FormRow>
          <FormColumn>
            <RackPermissionField
              fieldId="rackUse"
              requestStage={requestStage}
              requestId={requestId}
            >
              <FormInput
                label="Rack Use"
                value={formData.rackUse || ''}
                onChangeText={value => updateField('rackUse', value)}
                placeholder="Describe rack usage (e.g., Server, Network, Storage)"
                disabled={disabled}
                containerStyle={formFieldInStack}
              />
            </RackPermissionField>
          </FormColumn>
          <FormColumn>
            <RackPermissionField
              fieldId="rackQuantity"
              requestStage={requestStage}
              requestId={requestId}
            >
              <NumberInput
                label="Rack Quantity"
                value={String(formData.rackQuantity ?? '')}
                onChangeText={value =>
                  updateField('rackQuantity', parseOptionalNumber(value))
                }
                placeholder="Enter quantity of racks"
                min={1}
                showStepper
                disabled={disabled}
                containerStyle={formFieldInStack}
              />
            </RackPermissionField>
          </FormColumn>
        </FormRow>

        <FormRow>
          <FormColumn>
            <RackPermissionField
              fieldId="rackSeismicRated"
              requestStage={requestStage}
              requestId={requestId}
            >
              <FormYesNoChoice
                label="Seismic Rated"
                value={booleanToYesNo(formData.rackSeismicRated)}
                onSelect={v => updateField('rackSeismicRated', v === 'Yes')}
                disabled={disabled}
                testID={`${testID}-seismic-rated`}
                containerStyle={formFieldInStack}
              />
            </RackPermissionField>
          </FormColumn>
          <FormColumn>
            <RackPermissionField
              fieldId="rackMaxLoadRating"
              requestStage={requestStage}
              requestId={requestId}
            >
              <FormDropdownField
                label="Max Load Rating"
                value={formData.rackMaxLoadRating || ''}
                options={[...RACK_MAX_LOAD_RATING_OPTIONS]}
                onSelect={value =>
                  updateField(
                    'rackMaxLoadRating',
                    Array.isArray(value) ? value[0] ?? '' : value,
                  )
                }
                placeholder="Select..."
                disabled={disabled}
                allowClear
                containerStyle={formFieldInStack}
              />
            </RackPermissionField>
          </FormColumn>
        </FormRow>

        <FormRow>
          <FormColumn>
            <RackPermissionField
              fieldId="rackColor"
              requestStage={requestStage}
              requestId={requestId}
            >
              <FormDropdownField
                label="Color"
                value={formData.rackColor || ''}
                options={[...RACK_COLOR_OPTIONS]}
                onSelect={value =>
                  updateField(
                    'rackColor',
                    Array.isArray(value) ? value[0] ?? '' : value,
                  )
                }
                placeholder="Select..."
                disabled={disabled}
                allowClear
                containerStyle={formFieldInStack}
              />
            </RackPermissionField>
          </FormColumn>
          <FormColumn>
            <RackPermissionField
              fieldId="rackDimensionsHeight"
              requestStage={requestStage}
              requestId={requestId}
            >
              <NumberInput
                label={rackHeightLabel}
                value={String(formData.rackDimensionsHeight ?? '')}
                onChangeText={value =>
                  updateField(
                    'rackDimensionsHeight',
                    parseOptionalNumber(value),
                  )
                }
                placeholder="Enter height in RU (Standard/MGX) or OU (ORV3)"
                min={0}
                showStepper
                disabled={disabled}
                containerStyle={formFieldInStack}
              />
            </RackPermissionField>
          </FormColumn>
        </FormRow>

        <FormRow>
          <FormColumn>
            <RackPermissionField
              fieldId="rackDimensionsLength"
              requestStage={requestStage}
              requestId={requestId}
            >
              <UnitNumberField
                fieldKey="rackDimensionsLength"
                unitScopeId={formData.id}
                pair="lengthIn"
                label={depthLabel}
                value={String(formData.rackDimensionsLength ?? '')}
                onChangeText={value =>
                  updateField(
                    'rackDimensionsLength',
                    parseOptionalNumber(value),
                  )
                }
                placeholder="Enter rack depth"
                min={0}
                showStepper
                disabled={disabled}
                readOnlyMode={disabled}
                containerStyle={formFieldInStack}
              />
            </RackPermissionField>
          </FormColumn>
          <FormColumn>
            <RackPermissionField
              fieldId="rackDimensionsWidth"
              requestStage={requestStage}
              requestId={requestId}
            >
              <UnitNumberField
                fieldKey="rackDimensionsWidth"
                unitScopeId={formData.id}
                pair="lengthIn"
                label={widthLabel}
                value={String(formData.rackDimensionsWidth ?? '')}
                onChangeText={value =>
                  updateField('rackDimensionsWidth', parseOptionalNumber(value))
                }
                placeholder="Enter rack width"
                min={0}
                showStepper
                disabled={disabled}
                readOnlyMode={disabled}
                containerStyle={formFieldInStack}
              />
            </RackPermissionField>
          </FormColumn>
        </FormRow>

        <FormRow>
          <FormColumn>
            <RackPermissionField
              fieldId="rackRequiresLiquidToChip"
              requestStage={requestStage}
              requestId={requestId}
            >
              <FormDropdown
                label="Racks require Liquid to Chip cooling?"
                value={
                  formData.rackRequiresLiquidToChip === true
                    ? 'Yes'
                    : formData.rackRequiresLiquidToChip === false
                    ? 'No'
                    : ''
                }
                options={['Yes', 'No']}
                onSelect={value => {
                  const v = Array.isArray(value) ? value[0] : value;
                  updateField('rackRequiresLiquidToChip', v === 'Yes');
                }}
                placeholder="Select..."
                disabled={disabled}
                containerStyle={formFieldInStack}
              />
            </RackPermissionField>
          </FormColumn>
          <FormColumn>
            <RackPermissionField
              fieldId="rackDescribeElevations"
              requestStage={requestStage}
              requestId={requestId}
            >
              <FormInput
                label="Describe Rack Elevations"
                value={formData.rackDescribeElevations || ''}
                onChangeText={value =>
                  updateField('rackDescribeElevations', value)
                }
                placeholder="Describe rack configuration (e.g., Dell XE9680 x10, 2 per rack)"
                multiline
                maxLength={10000}
                richText
                requestId={requestId}
                requestSection={racksSectionId}
                isOnline={!isOffline}
                disabled={disabled}
                containerStyle={formFieldInStack}
              />
            </RackPermissionField>
          </FormColumn>
        </FormRow>
      </StepForm>

      <StepForm
        title={getSectionLabel(powerSectionId)}
        titleSubtitle={RACK_EDIT_POWER_CARD_SUBTITLE}
        defaultSectionExpanded={false}
        {...RACK_EDITOR_SUBSECTION_PERSIST}
        {...powerMetrics}
        sectionId={powerSectionId}
        requestId={requestId}
        requestStage={permissionStage}
        allowAttachments={attachmentsEnabled}
        attachments={powerAttachments}
        onAttachFiles={
          onAttachFiles ? () => onAttachFiles(powerSectionId) : undefined
        }
        onDeleteAttachment={onDeleteAttachment}
        selectedFiles={powerSelectedFiles}
        onFilesSelected={
          onFilesSelected
            ? files => onFilesSelected(powerSectionId, files)
            : undefined
        }
        isOffline={isOffline}
        onCaptionChange={onCaptionChange}
        pendingCaptionEdits={pendingCaptionEdits}
        isEditing={!disabled}
        disabled={disabled}
        containerStyle={styles.sectionCard}
      >
        <FormRow>
          <FormColumn>
            <RackPermissionField
              fieldId="rackPduManufacturer"
              requestStage={requestStage}
              requestId={requestId}
            >
              <FormInput
                label={RACK_POWER_FIELD_LABELS.rackPduManufacturer}
                value={formData.rackPduManufacturer || ''}
                onChangeText={value =>
                  updateField('rackPduManufacturer', value)
                }
                placeholder="Enter equipment manufacturer name"
                disabled={disabled}
                containerStyle={formFieldInStack}
              />
            </RackPermissionField>
          </FormColumn>
        </FormRow>

        <FormRow>
          <FormColumn>
            <RackPermissionField
              fieldId="rackPduModel"
              requestStage={requestStage}
              requestId={requestId}
            >
              <FormInput
                label={RACK_POWER_FIELD_LABELS.rackPduModel}
                value={formData.rackPduModel || ''}
                onChangeText={value => updateField('rackPduModel', value)}
                placeholder="Enter equipment model number"
                disabled={disabled}
                containerStyle={formFieldInStack}
              />
            </RackPermissionField>
          </FormColumn>
          <FormColumn>
            <RackPermissionField
              fieldId="rackPduMaxKwPerRack"
              requestStage={requestStage}
              requestId={requestId}
            >
              <FormInput
                label={RACK_POWER_FIELD_LABELS.rackPduMaxKwPerRack}
                value={formData.rackPduMaxKwPerRack || ''}
                onChangeText={value =>
                  updateField('rackPduMaxKwPerRack', value)
                }
                placeholder="Enter maximum kW per rack"
                disabled={disabled}
                containerStyle={formFieldInStack}
              />
            </RackPermissionField>
          </FormColumn>
        </FormRow>

        <FormRow>
          <FormColumn>
            <RackPermissionField
              fieldId="rackPduPowerType"
              requestStage={requestStage}
              requestId={requestId}
            >
              {/* #1012 Item 2b: web/mobile parity — single multi-select dropdown on
                every platform (was free-text on mobile). Migration 085 classifies
                legacy free-text rows into AC / DC / 'AC, DC' / ''. */}
              <FormDropdown
                label={RACK_POWER_FIELD_LABELS.rackPduPowerType}
                value={parsePowerType(formData.rackPduPowerType)}
                options={AC_DC_POWER_OPTIONS}
                multiple
                onSelect={value => {
                  const arr = Array.isArray(value) ? value : [];
                  updateField('rackPduPowerType', serializePowerType(arr));
                }}
                placeholder="Select..."
                disabled={disabled}
                containerStyle={formFieldInStack}
              />
            </RackPermissionField>
          </FormColumn>
          <FormColumn>
            <RackPermissionField
              fieldId="rackPduInputPlugType"
              requestStage={requestStage}
              requestId={requestId}
            >
              <FormInput
                label={RACK_POWER_FIELD_LABELS.rackPduInputPlugType}
                value={formData.rackPduInputPlugType || ''}
                onChangeText={value =>
                  updateField('rackPduInputPlugType', value)
                }
                placeholder="Specify input plug type (e.g., L6-30, C19)"
                disabled={disabled}
                containerStyle={formFieldInStack}
              />
            </RackPermissionField>
          </FormColumn>
        </FormRow>

        <FormRow>
          <FormColumn>
            <RackPermissionField
              fieldId="rackPduCountPerRack"
              requestStage={requestStage}
              requestId={requestId}
            >
              <NumberInput
                label={RACK_POWER_FIELD_LABELS.rackPduCountPerRack}
                value={formData.rackPduCountPerRack || ''}
                onChangeText={value =>
                  updateField('rackPduCountPerRack', value)
                }
                placeholder="Enter number of rPDUs per rack"
                min={0}
                showStepper
                disabled={disabled}
                containerStyle={formFieldInStack}
              />
            </RackPermissionField>
          </FormColumn>
          <FormColumn>
            <RackPermissionField
              fieldId="rackPduDcPowerShelves"
              requestStage={requestStage}
              requestId={requestId}
            >
              <NumberInput
                label={RACK_POWER_FIELD_LABELS.rackPduDcPowerShelves}
                value={String(formData.rackPduDcPowerShelves ?? '')}
                onChangeText={value =>
                  updateField(
                    'rackPduDcPowerShelves',
                    parseOptionalNumber(value),
                  )
                }
                placeholder="Enter number of DC power shelves"
                min={0}
                showStepper
                disabled={disabled}
                containerStyle={formFieldInStack}
              />
            </RackPermissionField>
          </FormColumn>
        </FormRow>

        <FormRow>
          <FormColumn>
            <RackPermissionField
              fieldId="rackPduOutletSwitchingMonitored"
              requestStage={requestStage}
              requestId={requestId}
            >
              <FormDropdownField
                label={RACK_POWER_FIELD_LABELS.rackPduOutletSwitchingMonitored}
                value={formData.rackPduOutletSwitchingMonitored || ''}
                options={[...RACK_PDU_OUTLET_TYPE_OPTIONS]}
                onSelect={value =>
                  updateField(
                    'rackPduOutletSwitchingMonitored',
                    Array.isArray(value) ? value[0] ?? '' : value,
                  )
                }
                placeholder="Select..."
                disabled={disabled}
                allowClear
                containerStyle={formFieldInStack}
              />
            </RackPermissionField>
          </FormColumn>
          <FormColumn>
            <RackPermissionField
              fieldId="rackPduCapacityAmps"
              requestStage={requestStage}
              requestId={requestId}
            >
              <NumberInput
                label={RACK_POWER_FIELD_LABELS.rackPduCapacityAmps}
                value={String(formData.rackPduCapacityAmps ?? '')}
                onChangeText={value =>
                  updateField('rackPduCapacityAmps', parseOptionalNumber(value))
                }
                placeholder="Enter amp capacity"
                min={0}
                showStepper
                disabled={disabled}
                containerStyle={formFieldInStack}
              />
            </RackPermissionField>
          </FormColumn>
        </FormRow>

        <FormRow>
          <FormColumn>
            <RackPermissionField
              fieldId="rackPduCapacityVolts"
              requestStage={requestStage}
              requestId={requestId}
            >
              <NumberInput
                label={RACK_POWER_FIELD_LABELS.rackPduCapacityVolts}
                value={String(formData.rackPduCapacityVolts ?? '')}
                onChangeText={value =>
                  updateField(
                    'rackPduCapacityVolts',
                    parseOptionalNumber(value),
                  )
                }
                placeholder="Enter voltage"
                min={0}
                showStepper
                acceptFreeText
                disabled={disabled}
                containerStyle={formFieldInStack}
              />
            </RackPermissionField>
          </FormColumn>
          <FormColumn>
            <RackPermissionField
              fieldId="rackPduCapacityKw"
              requestStage={requestStage}
              requestId={requestId}
            >
              <NumberInput
                label={RACK_POWER_FIELD_LABELS.rackPduCapacityKw}
                value={String(formData.rackPduCapacityKw ?? '')}
                onChangeText={value =>
                  updateField('rackPduCapacityKw', parseOptionalNumber(value))
                }
                placeholder="Enter kW capacity"
                min={0}
                showStepper
                disabled={disabled}
                containerStyle={formFieldInStack}
              />
            </RackPermissionField>
          </FormColumn>
        </FormRow>

        <FormRow>
          <FormColumn>
            <RackPermissionField
              fieldId="rackPduLoadReadingsKw"
              requestStage={requestStage}
              requestId={requestId}
            >
              <FormInput
                label={RACK_POWER_FIELD_LABELS.rackPduLoadReadingsKw}
                value={formData.rackPduLoadReadingsKw || ''}
                onChangeText={value =>
                  updateField('rackPduLoadReadingsKw', value)
                }
                placeholder="Enter load readings"
                disabled={disabled}
                containerStyle={formFieldInStack}
              />
            </RackPermissionField>
          </FormColumn>
          <FormColumn>
            <RackPermissionField
              fieldId="rackPduMountingType"
              requestStage={requestStage}
              requestId={requestId}
            >
              <FormDropdownField
                label={RACK_POWER_FIELD_LABELS.rackPduMountingType}
                value={formData.rackPduMountingType || ''}
                options={[...RACK_PDU_MOUNTING_TYPE_OPTIONS]}
                onSelect={value =>
                  updateField(
                    'rackPduMountingType',
                    Array.isArray(value) ? value[0] ?? '' : value,
                  )
                }
                placeholder="Select..."
                disabled={disabled}
                allowClear
                containerStyle={formFieldInStack}
              />
            </RackPermissionField>
          </FormColumn>
        </FormRow>

        <FormRow>
          <FormColumn>
            <RackPermissionField
              fieldId="rackPduOutletTypesQuantity"
              requestStage={requestStage}
              requestId={requestId}
            >
              <FormInput
                label={RACK_POWER_FIELD_LABELS.rackPduOutletTypesQuantity}
                value={formData.rackPduOutletTypesQuantity || ''}
                onChangeText={value =>
                  updateField('rackPduOutletTypesQuantity', value)
                }
                placeholder="Specify outlet types and quantities"
                disabled={disabled}
                containerStyle={formFieldInStack}
              />
            </RackPermissionField>
          </FormColumn>
          <FormColumn>
            <RackPermissionField
              fieldId="rackPduLocation"
              requestStage={requestStage}
              requestId={requestId}
            >
              <FormInput
                label={RACK_POWER_FIELD_LABELS.rackPduLocation}
                value={formData.rackPduLocation || ''}
                onChangeText={value => updateField('rackPduLocation', value)}
                placeholder="Describe PDU location"
                disabled={disabled}
                containerStyle={formFieldInStack}
              />
            </RackPermissionField>
          </FormColumn>
        </FormRow>
      </StepForm>

      <RequestFormAdditionalInformation
        shell="stepForm"
        titleSubtitle="Additional notes about rack configuration not captured above."
        sectionId={additionalSectionId}
        requestId={requestId}
        requestStage={permissionStage}
        allowAttachments={attachmentsEnabled}
        attachments={additionalAttachments}
        onAttachFiles={
          onAttachFiles ? () => onAttachFiles(additionalSectionId) : undefined
        }
        onDeleteAttachment={onDeleteAttachment}
        selectedFiles={additionalSelectedFiles}
        onFilesSelected={
          onFilesSelected
            ? files => onFilesSelected(additionalSectionId, files)
            : undefined
        }
        isOffline={isOffline}
        onCaptionChange={onCaptionChange}
        pendingCaptionEdits={pendingCaptionEdits}
        isEditing={!disabled}
        disabled={disabled}
        fieldId="rackAdditionalRackInformation"
        permissionRequestStage={permissionStage}
        showFullDetails={showFullDetails}
        inputLabel={RACK_POWER_FIELD_LABELS.rackAdditionalRackInformation}
        placeholder="Enter additional information"
        value={formData.rackAdditionalRackInformation || ''}
        onChangeText={value =>
          updateField('rackAdditionalRackInformation', value)
        }
        numberOfLines={3}
        webMultilineAutoSize
        maxLength={10000}
        inputUseEditableProp={false}
        inputDisabled={disabled}
        inputContainerStyle={formFieldInStack}
        wrapFieldInRowColumn={false}
        labelWithInfo={{
          legendText: RACK_POWER_FIELD_LABELS.rackAdditionalRackInformation,
          onInfo: () => setRacksHelpKey('rackAdditionalRackInformation'),
          infoA11yHint: rackSubsectionsRackInfoA11y(
            'rackAdditionalRackInformation',
          ).hint,
          a11yDescription: rackSubsectionsRackInfoA11y(
            'rackAdditionalRackInformation',
          ).description,
        }}
        subsectionFields={RACK_POWER_NOTES_SUBSECTION_FIELDS}
        headerValues={headerValues}
        containerStyle={styles.sectionCard}
        defaultSectionExpanded={false}
        {...RACK_EDITOR_SUBSECTION_PERSIST}
      />
    </FormStackColumnsOnWebProvider>
  );
};

const styles = StyleSheet.create({
  sectionCard: {
    marginBottom: spacing.md,
  },
});
